'use client';

import React, {
  ReactNode, createContext, useContext, useMemo,
} from 'react';
import { TileVisibilityConditions } from '@/integrations/crystallize/helpers/crystallize-tiles-helper';

// Carries the resolved tile-visibility conditions (payment success/failure, virtual order, club
// membership, logged-in) to any tile tagged with a matching `hide-<condition>` topic. This is a
// GENERIC mechanism — any surface may provide it — but TODAY only the order-confirmation page does;
// everywhere else the context is absent and the condition filter is a no-op. Kept as a domain-neutral
// context (not order-coupled) so the tile renderer stays generic and the hide feature can be reused
// on other surfaces later. A context, rather than a prop, is what lets the inputs reach tiles nested
// inside a container tile (e.g. `split-layout`) without threading them through PageTilesRenderer /
// TileRenderer.
const TileVisibilityConditionsContext = createContext<TileVisibilityConditions | undefined>(
  undefined,
);

interface TileVisibilityConditionsProviderProps {
  conditions: TileVisibilityConditions;
  children: ReactNode;
}

export function TileVisibilityConditionsProvider({
  conditions,
  children,
}: Readonly<TileVisibilityConditionsProviderProps>) {
  const {
    isPaymentFailure, isPaymentSuccess, isVirtualOrder, isNukiClubMember, isLoggedIn,
  } =
    conditions;

  // Memoise on the primitive inputs so a fresh conditions object from the parent (built inline each
  // render) doesn't re-render every tile consumer on every parent render.
  const value = useMemo(
    () => ({
      isPaymentFailure, isPaymentSuccess, isVirtualOrder, isNukiClubMember, isLoggedIn,
    }),
    [isPaymentFailure, isPaymentSuccess, isVirtualOrder, isNukiClubMember, isLoggedIn],
  );

  return (
    <TileVisibilityConditionsContext.Provider value={value}>
      {children}
    </TileVisibilityConditionsContext.Provider>
  );
}

/** The current surface's tile-visibility conditions, or `undefined` when no provider is present. */
export function useTileVisibilityConditions(): TileVisibilityConditions | undefined {
  return useContext(TileVisibilityConditionsContext);
}
